import React, { useState } from 'react';
import { rewardService, uploadService } from '../api/services';
import AdminModal from './AdminModal';
import ImageCropEditor from './ImageCropEditor';
import './Reward.css';

// 兌換商品上架/編輯 pop up。item 為 null 時為新增，否則為編輯。
// 圖片支援直接貼 URL 或上傳檔案；上傳前會先進入裁切編輯器（可縮放、調整位置），裁切後再上傳回填 image_url。庫存留空＝無限量。
const RewardItemModal = ({ item, onClose, onSaved }) => {
  const isEdit = !!item;
  const [form, setForm] = useState({
    name: item?.name || '',
    description: item?.description || '',
    image_url: item?.image_url || '',
    points_cost: item?.points_cost ?? 0,
    // 庫存：null 顯示為空字串（無限量）
    stock: item?.stock === null || item?.stock === undefined ? '' : String(item.stock),
    status: item?.status || 'ACTIVE',
    sort_order: item?.sort_order ?? 0,
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  // 待裁切的原始檔案：選檔後先進入裁切編輯器，確認後才上傳
  const [cropFile, setCropFile] = useState(null);

  // 選檔：驗證為圖片後開啟裁切編輯器（尚未上傳）
  const pickFile = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('請選擇圖片檔案'); return; }
    setError('');
    setCropFile(file);
  };

  // 裁切確認：將裁切後的 Blob 上傳並回填 image_url
  const handleCropConfirm = async (blob) => {
    setCropFile(null);
    setUploading(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('purpose', 'reward');
      fd.append('files', blob, 'reward.jpg');
      const res = await uploadService.upload(fd);
      const url = res.data?.[0]?.url;
      if (url) setForm((p) => ({ ...p, image_url: url }));
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError('請填寫商品名稱'); return; }
    setBusy(true);
    setError('');
    // 庫存空字串送出時不帶 stock（後端視為無限量）；有值才帶數字
    const payload = {
      name: form.name.trim(),
      description: form.description,
      image_url: form.image_url,
      points_cost: Number(form.points_cost) || 0,
      stock: String(form.stock).trim() === '' ? '' : Number(form.stock),
      status: form.status,
      sort_order: Number(form.sort_order) || 0,
    };
    try {
      if (isEdit) await rewardService.update(item.id, payload);
      else await rewardService.create(payload);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setBusy(false);
    }
  };

  return (
    <AdminModal title={isEdit ? '編輯兌換商品' : '上架兌換商品'} onClose={onClose}>
      {error && <div className="alert alert-danger">{error}</div>}
      <div className="admin-form-row" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 12 }}>
        <label>商品名稱 *</label>
        <input className="form-control form-control-sm" value={form.name} onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))} placeholder="商品名稱" />
        <label>說明</label>
        <textarea className="form-control form-control-sm" rows={3} value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} placeholder="商品說明（選填）" />
        <label>圖片 URL</label>
        <input className="form-control form-control-sm" value={form.image_url} onChange={(e) => setForm((p) => ({ ...p, image_url: e.target.value }))} placeholder="https://... 或於下方上傳" />
        <label>上傳並裁切圖片</label>
        <input className="form-control form-control-sm" type="file" accept="image/*" disabled={uploading} onChange={(e) => { pickFile(e.target.files?.[0]); e.target.value = ''; }} />
        {uploading && <div className="setting-desc">圖片上傳中...</div>}
        {form.image_url && <img src={form.image_url} alt="預覽" style={{ width: 120, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border-color)' }} />}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 120 }}>
            <label>所需點數 *</label>
            <input className="form-control form-control-sm" type="number" min="0" value={form.points_cost} onChange={(e) => setForm((p) => ({ ...p, points_cost: e.target.value }))} />
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <label>庫存（留空＝無限）</label>
            <input className="form-control form-control-sm" type="number" min="0" value={form.stock} onChange={(e) => setForm((p) => ({ ...p, stock: e.target.value }))} placeholder="無限量" />
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <label>排序</label>
            <input className="form-control form-control-sm" type="number" value={form.sort_order} onChange={(e) => setForm((p) => ({ ...p, sort_order: e.target.value }))} />
          </div>
          <div style={{ flex: 1, minWidth: 120 }}>
            <label>狀態</label>
            <select className="form-control form-control-sm" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value }))}>
              <option value="ACTIVE">上架</option>
              <option value="DISABLED">下架（停用）</option>
            </select>
          </div>
        </div>
        <div className="btn-group" style={{ justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn btn-sm btn-primary" disabled={busy || uploading} onClick={handleSave}>{busy ? '儲存中...' : '儲存'}</button>
          <button className="btn btn-sm btn-secondary" disabled={busy} onClick={onClose}>取消</button>
        </div>
      </div>
      {cropFile && (
        <ImageCropEditor
          file={cropFile}
          aspect={4 / 3}
          outputWidth={800}
          onConfirm={handleCropConfirm}
          onCancel={() => setCropFile(null)}
        />
      )}
    </AdminModal>
  );
};

export default RewardItemModal;
